import type React from "react";
import { useConvex, useQuery } from "convex/react";
import {
  ChevronDown,
  Clock,
  FileImage,
  Film,
  Download,
  FolderOpen,
  History,
  Image,
  ImageUp,
  RotateCcw,
  Search,
  Sparkles,
  Tag,
  Trash2,
  Upload,
  X,
  Copy,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { UploadPreferencesDialog } from "@/components/editor/UploadPreferencesDialog";
import type { UploadPreference } from "@/components/editor/UploadPreferencesDialog";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ProgressiveImage } from "@/components/ui/progressive-image";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useIndexedDBCache } from "@/hooks/useIndexedDBCache";
import {
  useVersionsByEntity,
  createVersion as createLocalVersion,
  revertToVersion as revertLocalVersion,
  removeVersion as removeLocalVersion,
  getNextVersionNumber as getNextLocalVersionNumber,
} from "@/data/adapters/local/assetVersions";
import type { LocalAssetVersion, AssetEntityType } from "@/data/adapters/local/assetVersions";
import { updatePanorama as updateLocalPanorama } from "@/data/adapters/local/panoramas";
import { updateHotspot as updateLocalHotspot } from "@/data/adapters/local/hotspots";
import { computeFileHash } from "@/utils/fileHash";
import { StorageServiceFactory } from "@/services/storage/StorageServiceFactory";
import { getVideoDimensions } from "@/utils/videoUtils";
import { getImageDimensions } from "@/utils/imageDimensions";
import { isElectron, LocalFileSystemService } from "@/services/storage/LocalFileSystemClient";
import { StorageService } from "@/services/storageService";
import { useEditorStore } from "@/store/editorStore";
import { StoragePaths, resolveStoragePathSegments } from "@/utils/storagePaths";
import { regeneratePanoramaThumbnails } from "@/utils/thumbnailRegenerator";
import { api } from "../../convex/_generated/api";

// Custom icon for Assets page
const AssetsIcon = ({ size = 16, className = "" }: { size?: number; className?: string }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <title>Assets</title>
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);

export { AssetsIcon };

function formatFileSize(bytes: number | undefined): string {
  if (bytes === undefined) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const typeIcons: Record<string, typeof Image> = {
  panorama: FileImage,
  icon: Sparkles,
  image: Image,
  video: Film,
  download: Download,
};

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

function sanitizePanoramaName(name: string) {
  return name
    .replace(/\.[^/.]+$/, "")
    .replace(/[^a-zA-Z0-9-_]/g, "_")
    .toLowerCase();
}

const isUuid = (value: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value,
  );

const getFileExtension = (value: string | undefined | null) => {
  if (!value) return "";
  const clean = value.split("?")[0].split("#")[0];
  const name = clean.split("/").pop() || clean;
  const parts = name.split(".");
  if (parts.length < 2) return "";
  const ext = parts.pop() || "";
  return ext.toLowerCase();
};

function normalizeStoragePath(value: string): { isAbsolute: boolean; path: string } {
  if (!value) return { isAbsolute: false, path: value };
  if (value.startsWith("/")) return { isAbsolute: true, path: value };

  if (value.startsWith("app://storage/")) {
    return { isAbsolute: false, path: value.replace("app://storage/", "") };
  }
  if (value.startsWith("app:/storage/")) {
    return { isAbsolute: false, path: value.replace("app:/storage/", "") };
  }
  return { isAbsolute: false, path: value };
}

async function getLocalFileSize(storagePath: string): Promise<number | undefined> {
  if (!isElectron() || !window.electronAPI) return undefined;
  try {
    const normalized = normalizeStoragePath(storagePath);
    const fullPath = normalized.isAbsolute
      ? normalized.path
      : await window.electronAPI.getStoragePath(normalized.path);
    const stats = await window.electronAPI.statFile(fullPath);
    return typeof stats.size === "number" ? stats.size : undefined;
  } catch {
    return undefined;
  }
}

interface AssetItem {
  id: string;
  name: string;
  entityType: AssetEntityType;
  thumbnailUrl?: string;
  width?: number;
  height?: number;
  fileSize?: number;
  currentVersionNumber?: number;
  content?: Record<string, unknown>;
}

// ─── Version Row ──────────────────────────────────────────────────────────────

function VersionRow({
  version,
  isCurrent,
  onRevert,
  onDelete,
  onLabel,
}: {
  version: LocalAssetVersion;
  isCurrent: boolean;
  onRevert: () => void;
  onDelete: () => void;
  onLabel: () => void;
}) {
  const isCurrentBool = Boolean(isCurrent);
  const rawLabel = (version as any).label;
  const labelText =
    typeof rawLabel === "string"
      ? rawLabel.replace(/[\s\u200B-\u200D\uFEFF]+/g, " ").trim()
      : typeof rawLabel === "number"
        ? String(rawLabel)
        : "";
  const showLabel = labelText !== "" && labelText !== "0";
  const showHeader = showLabel || isCurrentBool;

  return (
    <div
      className={`flex items-center gap-3 px-3 py-2 rounded text-xs ${
        isCurrentBool
          ? "bg-blue-600/20 border border-blue-500/30"
          : "bg-gray-700 hover:bg-gray-650"
      }`}
    >
      <div className="flex-shrink-0 w-6 text-center text-gray-400 font-mono">
        v{version.version_number}
      </div>

      <div className="flex-1 min-w-0">
        {showHeader && (
          <div className="flex items-center gap-2">
            {showLabel && <span className="text-blue-300 font-medium truncate">{labelText}</span>}
            {isCurrentBool && (
              <span className="px-1.5 py-0.5 rounded bg-blue-600/30 text-blue-300 text-[10px] font-medium">
                current
              </span>
            )}
          </div>
        )}
        <div className="text-gray-400 mt-0.5">
          {formatFileSize(version.file_size)}
          {version.width && version.height && ` · ${version.width}×${version.height}`}
          {version.created_at && ` · ${formatDate(version.created_at)}`}
        </div>
      </div>

      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          type="button"
          onClick={onLabel}
          className="p-1 rounded hover:bg-gray-600 text-gray-400 hover:text-white transition-colors"
          title="Add label"
        >
          <Tag size={12} />
        </button>
        {!isCurrentBool && (
          <button
            type="button"
            onClick={onRevert}
            className="p-1 rounded hover:bg-gray-600 text-gray-400 hover:text-white transition-colors"
            title="Revert to this version"
          >
            <RotateCcw size={12} />
          </button>
        )}
        {!isCurrentBool && (
          <button
            type="button"
            onClick={onDelete}
            className="p-1 rounded hover:bg-gray-600 text-gray-400 hover:text-red-400 transition-colors"
            title="Delete version"
          >
            <Trash2 size={12} />
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Asset Card ───────────────────────────────────────────────────────────────

function AssetCard({
  asset,
  useLocalBackend,
  isSelected,
  selectedCount,
  onSelect,
  onRename,
  onDelete,
}: {
  asset: AssetItem;
  useLocalBackend: boolean;
  isSelected: boolean;
  selectedCount: number;
  onSelect: (asset: AssetItem, e: React.MouseEvent) => void;
  onRename: (asset: AssetItem) => void;
  onDelete: (asset: AssetItem) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [labelDialogOpen, setLabelDialogOpen] = useState(false);
  const [labelTarget, setLabelTarget] = useState<string | null>(null);
  const [labelValue, setLabelValue] = useState("");
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [replacing, setReplacing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const convex = useConvex();
  const { toast } = useToast();
  const { clearPanoramaCache } = useIndexedDBCache();
  const { project, updatePanorama, updateSkinComponent, updateHotspot } = useEditorStore();
  const craftspaceId = project?.craftspace_id || "default";
  const useLocalPanorama =
    useLocalBackend || (asset.entityType === "panorama" && isUuid(asset.id));

  // For local mode, use the hook
  const localVersions = useVersionsByEntity(asset.entityType, asset.id, craftspaceId);
  // For cloud mode, use Convex query
  const cloudVersions = useQuery(
    api.assetVersions.listByEntity,
    !useLocalPanorama ? { entity_type: asset.entityType, entity_id: asset.id } : "skip",
  );

  const versions = useLocalPanorama ? localVersions : cloudVersions;
  const hasVersions = versions && versions.length > 0;
  const versionFileSize = Array.isArray(versions)
    ? versions.find((v) => typeof v?.file_size === "number")?.file_size
    : undefined;
  const displayFileSize = asset.fileSize ?? versionFileSize;

  const Icon = typeIcons[asset.entityType] || Image;
  const downloadMeta = asset.entityType === "download" ? asset.content || {} : {};
  const downloadName =
    (downloadMeta as Record<string, unknown>).downloadFileName as string | undefined;
  const downloadUrl =
    (downloadMeta as Record<string, unknown>).downloadFileUrl as string | undefined;
  const downloadExt =
    getFileExtension(downloadName || "") || getFileExtension(downloadUrl || "");

  const handleRevert = async (version: any) => {
    try {
      const versionId = version._id || version.id;
      if (useLocalPanorama) {
        await revertLocalVersion(versionId);
      } else {
        await convex.mutation(api.assetVersions.revert, { version_id: versionId as any });
      }

      if (asset.entityType === "panorama") {
        const patch: Record<string, unknown> = {};
        if (version.file_url) patch.file_url = version.file_url;
        if (version.private_file_url) patch.private_file_url = version.private_file_url;
        if (version.file_size) patch.file_size = version.file_size;
        if (version.width) patch.width = version.width;
        if (version.height) patch.height = version.height;
        if (Object.keys(patch).length > 0) {
          updatePanorama(asset.id, patch as any);
        }

        await clearPanoramaCache(asset.id);

        if (useLocalPanorama && project) {
          const sourceUrl = version.private_file_url || version.file_url;
          if (sourceUrl) {
            try {
              const { generateClientThumbnails, uploadClientThumbnails } = await import(
                "@/utils/clientThumbnailGenerator"
              );
              const response = await fetch(sourceUrl);
              if (response.ok) {
                const blob = await response.blob();
                const file = new File([blob], asset.name, {
                  type: blob.type || "image/jpeg",
                });
                const thumbnails = await generateClientThumbnails(file, 1024, 512);
                const projectId =
                  typeof project.id === "string" ? project.id : (project as any)._id;
                const craftspaceId = project.craftspace_id || "default";
                const panoramaName = sanitizePanoramaName(asset.name);
                const thumbnailUrls = await uploadClientThumbnails(
                  thumbnails,
                  craftspaceId,
                  projectId,
                  panoramaName,
                  storageSegments,
                );
                await updateLocalPanorama({
                  id: asset.id,
                  craftspace_id: craftspaceId,
                  thumbnail_url: thumbnailUrls.thumbnail_url,
                  thumbnail_url_webp: thumbnailUrls.thumbnail_url_webp ?? undefined,
                });
                updatePanorama(asset.id, {
                  thumbnail_url: thumbnailUrls.thumbnail_url,
                  thumbnail_url_webp: thumbnailUrls.thumbnail_url_webp ?? undefined,
                } as any);
                await clearPanoramaCache(asset.id);
              }
            } catch (thumbError) {
              console.warn("Local thumbnail regeneration failed:", thumbError);
            }
          }
        }

        if (!useLocalPanorama && project && !isUuid(asset.id)) {
          const projectId = (project as any)._id || project.id;
          const craftspaceId = project.craftspace_id || "default";
          const currentPanorama = await convex.query(api.panoramas.get, { id: asset.id as any });
          if (currentPanorama) {
            const result = await regeneratePanoramaThumbnails(
              currentPanorama,
              projectId,
              craftspaceId,
            );
            if (result.success) {
              updatePanorama(asset.id, {
                thumbnail_url: result.thumbnail_url,
                thumbnail_url_webp: result.thumbnail_url_webp,
              } as any);
              await clearPanoramaCache(asset.id);
            }
          }
        }
        const latest = await convex.query(api.panoramas.get, { id: asset.id as any });
        if (latest) {
          updatePanorama(asset.id, latest as any);
        }
      } else if (asset.entityType === "video") {
        const fileUrl = version.private_file_url || version.file_url;
        if (fileUrl) {
          const currentContent = (asset.content as Record<string, unknown>) || {};
          const nextContent = { ...currentContent, videoUrl: fileUrl };
          updateHotspot(asset.id, { content: nextContent } as any);
          if (useLocalBackend) {
            const craftspaceId = project?.craftspace_id || "default";
            await updateLocalHotspot({ id: asset.id, craftspace_id: craftspaceId, content: nextContent });
          } else {
            await convex.mutation(api.hotspots.update, {
              id: asset.id as any,
              content: nextContent,
            });
          }
        }
      } else if (asset.entityType === "download") {
        const fileUrl = version.private_file_url || version.file_url;
        if (fileUrl) {
          const currentContent = (asset.content as Record<string, unknown>) || {};
          const nextContent = {
            ...currentContent,
            downloadFileUrl: fileUrl,
            downloadFileName: version.original_filename || currentContent.downloadFileName,
          };
          updateHotspot(asset.id, { content: nextContent } as any);
          if (useLocalBackend) {
            const craftspaceId = project?.craftspace_id || "default";
            await updateLocalHotspot({ id: asset.id, craftspace_id: craftspaceId, content: nextContent });
          } else {
            await convex.mutation(api.hotspots.update, {
              id: asset.id as any,
              content: nextContent,
            });
          }
        }
      }

      toast({ title: "Reverted", description: "Asset reverted to selected version." });
    } catch (error) {
      console.error("Revert error:", error);
      toast({ variant: "destructive", title: "Error", description: "Failed to revert version." });
    }
  };

  const handleDelete = async (versionId: string) => {
    try {
      if (useLocalBackend) {
        await removeLocalVersion(versionId);
      } else {
        await convex.mutation(api.assetVersions.remove, { id: versionId as any });
      }
      toast({ title: "Deleted", description: "Version deleted." });
    } catch (error) {
      console.error("Delete error:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete version.",
      });
    }
  };

  const handleLabelSave = async () => {
    if (!labelTarget) return;
    // TODO: Add label mutation (update asset_version label field)
    setLabelDialogOpen(false);
    setLabelTarget(null);
    setLabelValue("");
  };

  const handleRename = async () => {
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === asset.name) {
      setRenameDialogOpen(false);
      return;
    }
    try {
      if (asset.entityType === "panorama") {
        // Update panorama name in store + backend
        updatePanorama(asset.id, { name: trimmed });
        if (useLocalBackend) {
          const craftspaceId = project?.craftspace_id || "default";
          await updateLocalPanorama({ id: asset.id, craftspace_id: craftspaceId, name: trimmed });
        } else {
          await convex.mutation(api.panoramas.update, { id: asset.id as any, name: trimmed });
        }
      } else if (asset.entityType === "video") {
        updateHotspot(asset.id, { name: trimmed } as any);
        if (useLocalBackend) {
          const craftspaceId = project?.craftspace_id || "default";
          await updateLocalHotspot({ id: asset.id, craftspace_id: craftspaceId, name: trimmed });
        } else {
          await convex.mutation(api.hotspots.update, { id: asset.id as any, name: trimmed });
        }
      } else if (asset.entityType === "download") {
        updateHotspot(asset.id, { name: trimmed } as any);
        if (useLocalBackend) {
          const craftspaceId = project?.craftspace_id || "default";
          await updateLocalHotspot({ id: asset.id, craftspace_id: craftspaceId, name: trimmed });
        } else {
          await convex.mutation(api.hotspots.update, { id: asset.id as any, name: trimmed });
        }
      } else {
        // Skin component (image/icon) — update via store
        updateSkinComponent(asset.id, { name: trimmed });
      }
      toast({ title: "Renamed", description: `Asset renamed to "${trimmed}".` });
    } catch (error) {
      console.error("Rename error:", error);
      toast({ variant: "destructive", title: "Error", description: "Failed to rename asset." });
    }
    setRenameDialogOpen(false);
  };

  const handleReplaceFile = async (file: File) => {
    if (!project) return;
    setReplacing(true);
    try {
      if (asset.entityType === "video") {
        const craftspaceId = project.craftspace_id || "default";
        const projectId = (project as any)._id || project.id;
        const entityId = asset.id;
        const storageService = await StorageServiceFactory.createStorageService(craftspaceId);

        const fileHash = await computeFileHash(file);
        let dimensions: { width: number; height: number } | null = null;
        try {
          dimensions = await getVideoDimensions(file);
        } catch (_error) {
          dimensions = null;
        }

        let nextVersion = 1;
        if (useLocalBackend) {
          nextVersion = await getNextLocalVersionNumber("video", entityId);
        } else {
          const existing = await convex.query(api.assetVersions.listByEntity, {
            entity_type: "video",
            entity_id: entityId,
          });
          const maxVersion = (existing || []).reduce(
            (max: number, v: any) => Math.max(max, v.version_number || 0),
            0,
          );
          nextVersion = maxVersion + 1;
        }

        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
        const versionPath = StoragePaths.private.assetVersion(
          storageSegments.craftspaceKey,
          storageSegments.projectKey,
          "video",
          entityId,
          nextVersion,
          `${Date.now()}_${safeName}`,
        );

        await storageService.uploadFile(file, versionPath);
        const fileUrl = await storageService.getFileUrl(versionPath);

        const currentContent = (asset.content as Record<string, unknown>) || {};
        const nextContent = { ...currentContent, videoUrl: fileUrl };

        if (useLocalBackend) {
          await updateLocalHotspot({ id: asset.id, craftspace_id: craftspaceId, content: nextContent });
          await createLocalVersion({
            project_id: projectId,
            entity_type: "video",
            entity_id: entityId,
            file_url: fileUrl,
            private_file_url: fileUrl,
            file_hash: fileHash,
            file_size: file.size,
            width: dimensions?.width || undefined,
            height: dimensions?.height || undefined,
            original_filename: file.name,
          });
        } else {
          await convex.mutation(api.hotspots.update, {
            id: asset.id as any,
            content: nextContent,
          });
          await convex.mutation(api.assetVersions.create, {
            project_id: projectId as any,
            entity_type: "video",
            entity_id: entityId,
            file_url: fileUrl,
            private_file_url: fileUrl,
            file_hash: fileHash,
            file_size: file.size,
            width: dimensions?.width || undefined,
            height: dimensions?.height || undefined,
            original_filename: file.name,
            metadata: { source: "assets-replace" },
          });
        }

        updateHotspot(asset.id, { content: nextContent } as any);
        toast({ title: "Replaced", description: "Video replaced. New version created." });
        return;
      }

      if (asset.entityType === "panorama") {
        // For panoramas, convert to data URL for preview, get dimensions, create version
        const dimensions = await getImageDimensions(file);
        const fileHash = await computeFileHash(file);
        const updates: Record<string, unknown> = {
          file_size: file.size,
          width: dimensions.width,
          height: dimensions.height,
        };

        if (useLocalPanorama) {
          const localStorageSvc = new LocalFileSystemService();
          const wsId = project.craftspace_id || "default";
          const panoramaName = sanitizePanoramaName(asset.name || file.name);
          const timestamp = Date.now();
          const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
          const filePath = StoragePaths.private.panoramaOriginal(
            storageSegments.craftspaceKey,
            storageSegments.projectKey,
            panoramaName,
            `${timestamp}_${safeName}`,
          );
          await localStorageSvc.uploadFile(file, filePath);
          const fileUrl = await localStorageSvc.getFileUrl(filePath);

          let thumbnailUrls: { thumbnail_url: string; thumbnail_url_webp: string | null } | null =
            null;
          try {
            const { generateClientThumbnails, uploadClientThumbnails } = await import(
              "@/utils/clientThumbnailGenerator"
            );
            const thumbnails = await generateClientThumbnails(file, 1024, 512);
            thumbnailUrls = await uploadClientThumbnails(
              thumbnails,
              wsId,
              project.id,
              panoramaName,
              storageSegments,
            );
          } catch (thumbErr) {
            console.warn("Local thumbnail generation failed:", thumbErr);
          }

          await updateLocalPanorama({
            id: asset.id,
            craftspace_id: wsId,
            file_url: fileUrl,
            private_file_url: fileUrl,
            file_size: file.size,
            width: dimensions.width,
            height: dimensions.height,
            thumbnail_url: thumbnailUrls?.thumbnail_url,
            thumbnail_url_webp: thumbnailUrls?.thumbnail_url_webp ?? undefined,
          });
          await createLocalVersion({
            project_id: typeof project.id === "string" ? project.id : (project as any)._id,
            entity_type: "panorama",
            entity_id: asset.id,
            file_url: fileUrl,
            private_file_url: fileUrl,
            file_hash: fileHash,
            file_size: file.size,
            width: dimensions.width,
            height: dimensions.height,
            original_filename: file.name,
          });
          updatePanorama(asset.id, {
            ...updates,
            file_url: fileUrl,
            private_file_url: fileUrl,
            thumbnail_url: thumbnailUrls?.thumbnail_url,
            thumbnail_url_webp: thumbnailUrls?.thumbnail_url_webp ?? undefined,
          } as any);
          await clearPanoramaCache(asset.id);
          toast({ title: "Replaced", description: "Panorama replaced. New version created." });
        } else {
          const craftspaceId = project.craftspace_id || "default";
          const projectId = (project as any)._id || project.id;
          const panoramaName = sanitizePanoramaName(asset.name || file.name);
          const filename = `${Date.now()}_${file.name}`;
          const privatePath = StoragePaths.private.panoramaOriginal(
            storageSegments.craftspaceKey,
            storageSegments.projectKey,
            panoramaName,
            filename,
          );

          await StorageService.uploadPrivate("staging", privatePath, file, { upsert: true });
          const {
            data: { signedUrl },
          } = await StorageService.getPrivateUrl("staging", privatePath);

          await convex.mutation(api.panoramas.update, {
            id: asset.id as any,
            private_file_url: signedUrl,
            file_status: "private",
            file_size: file.size,
            width: dimensions.width,
            height: dimensions.height,
            processing_status: "pending",
          });

          updatePanorama(asset.id, {
            private_file_url: signedUrl,
            file_status: "private",
            file_size: file.size,
            width: dimensions.width,
            height: dimensions.height,
            processing_status: "pending",
          } as any);

          try {
            const { compressPanorama } = await import("@/utils/clientPanoramaCompressor");
            const { generateClientThumbnails, uploadClientThumbnails } = await import(
              "@/utils/clientThumbnailGenerator"
            );

            const compressionResult = await compressPanorama(file, {
              quality: 0.85,
              format: "webp",
              targetFileSize: 5 * 1024 * 1024,
            });

            const thumbnails = await generateClientThumbnails(file);

            const compressedPath = StoragePaths.private.panoramaProcessed(
              storageSegments.craftspaceKey,
              storageSegments.projectKey,
              panoramaName,
              `compressed_${panoramaName}${compressionResult.extension}`,
            );
            await StorageService.uploadPrivate("staging", compressedPath, compressionResult.compressed, {
              upsert: true,
            });

            const {
              data: { signedUrl: compressedFileUrl },
            } = await StorageService.getPrivateUrl("staging", compressedPath);

            const thumbnailUrls = await uploadClientThumbnails(
              thumbnails,
              craftspaceId,
              projectId,
              panoramaName,
              storageSegments,
            );

            await convex.mutation(api.panoramas.update, {
              id: asset.id as any,
              private_file_url: compressedFileUrl,
              thumbnail_url: thumbnailUrls.thumbnail_url,
              thumbnail_url_webp: thumbnailUrls.thumbnail_url_webp,
              processing_status: "completed",
              file_status: "public",
              file_size: compressionResult.compressed.size,
              data: {
                original_file_size: file.size,
                compressed_file_size: compressionResult.compressed.size,
                compression_ratio: compressionResult.compressionRatio,
                is_compressed: true,
                processing_method: "client-side",
              },
            });

            updatePanorama(asset.id, {
              private_file_url: compressedFileUrl,
              thumbnail_url: thumbnailUrls.thumbnail_url,
              thumbnail_url_webp: thumbnailUrls.thumbnail_url_webp,
              processing_status: "completed",
              file_status: "public",
              file_size: compressionResult.compressed.size,
              data: {
                original_file_size: file.size,
                compressed_file_size: compressionResult.compressed.size,
                compression_ratio: compressionResult.compressionRatio,
                is_compressed: true,
                processing_method: "client-side",
              },
            } as any);
          } catch (processingError) {
            console.error("Client-side processing error:", processingError);
            toast({
              variant: "destructive",
              title: "Processing warning",
              description:
                "Panorama uploaded successfully but processing failed. It may not have thumbnails.",
            });
          }

          await convex.mutation(api.assetVersions.create, {
            project_id: projectId,
            entity_type: "panorama",
            entity_id: asset.id as any,
            file_url: signedUrl,
            private_file_url: signedUrl,
            file_hash: fileHash,
            file_size: file.size,
            width: dimensions.width,
            height: dimensions.height,
            original_filename: file.name,
            metadata: { processing_method: "client-side" },
          } as any);

          await clearPanoramaCache(asset.id);
          toast({ title: "Replaced", description: "Panorama replaced. New version created." });
        }
      } else {
        // Skin component (image/icon) — convert to data URL and update content.src
        const dataUrl = await fileToDataUrl(file);
        const existing = useEditorStore.getState().skinComponents.find((c) => c.id === asset.id);
        updateSkinComponent(asset.id, {
          content: {
            ...(existing?.content || {}),
            src: dataUrl,
          },
        });
        toast({ title: "Replaced", description: "Asset image replaced." });
      }
    } catch (error) {
      console.error("Replace error:", error);
      toast({ variant: "destructive", title: "Error", description: "Failed to replace asset." });
    } finally {
      setReplacing(false);
    }
  };

  const acceptTypes =
    asset.entityType === "panorama"
      ? "image/jpeg,image/png,image/webp"
      : asset.entityType === "video"
        ? "video/*"
        : "image/jpeg,image/png,image/webp,image/svg+xml";

  return (
    <>
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <div
            className={`bg-gray-700 overflow-hidden cursor-pointer transition-colors border ${
              isSelected ? "border-2 border-blue-400" : "border-gray-500/50"
            }`}
            onClick={(e) => onSelect(asset, e)}
          >
            <Card className="bg-gray-700 overflow-hidden border-0">
            <CardContent className="p-0 flex flex-col">
              {/* Thumbnail area with hover overlay */}
              <div className="relative group border-b border-gray-600">
            <AspectRatio ratio={2 / 1} className="bg-gray-600">
              {asset.thumbnailUrl ? (
                <ProgressiveImage
                  src={asset.thumbnailUrl}
                  alt={asset.name}
                  className="w-full h-full object-contain"
                />
              ) : asset.entityType === "download" ? (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="w-20 h-24 rounded-lg bg-gray-700 border border-gray-600 flex flex-col items-center justify-center">
                    <div className="text-[10px] text-gray-300 uppercase tracking-widest">
                      {downloadExt || "FILE"}
                    </div>
                    <Download size={20} className="text-gray-400 mt-2" />
                  </div>
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Icon size={32} className="text-gray-500" />
                </div>
              )}
            </AspectRatio>
          </div>

          {/* Metadata section */}
          <div className="p-3 flex-1 flex flex-col">
            <h3 className="text-sm font-medium text-white truncate mb-1">{asset.name}</h3>
            <div className="text-xs text-gray-400 mb-2">
              {formatFileSize(displayFileSize)}
              {asset.width && asset.height ? ` · ${asset.width}×${asset.height}` : ""}
            </div>

            {/* Action buttons section */}
            <div className="flex items-center gap-2 mt-auto flex-wrap">
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      fileInputRef.current?.click();
                    }}
                    className="px-2 py-1 rounded bg-gray-600 hover:bg-gray-500 text-gray-300 hover:text-white transition-colors text-xs flex items-center gap-1"
                    disabled={replacing}
                  >
                    <ImageUp size={12} className={replacing ? "animate-spin" : ""} />
                    Replace
                  </button>
                </TooltipTrigger>
                <TooltipContent>Replace asset</TooltipContent>
              </Tooltip>

              {/* Version badge */}
              {hasVersions && (
                <button
                  type="button"
                  role="button"
                  tabIndex={0}
                  onClick={() => setExpanded(!expanded)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setExpanded((prev) => !prev);
                    }
                  }}
                  className="px-2 py-1 rounded-full bg-gray-600 text-gray-300 text-xs flex items-center gap-1 hover:bg-gray-500 transition-colors"
                >
                  <History size={10} />
                  {versions.length} versions
                </button>
              )}
            </div>
          </div>

          {/* Hidden file input for replace */}
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptTypes}
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleReplaceFile(file);
              e.target.value = "";
            }}
          />

          {/* Expanded version list */}
          {expanded && (
            <div className="border-t border-gray-600 px-3 py-3 space-y-2 max-h-64 overflow-y-auto">
              {hasVersions ? (
                versions.map((v: any) => (
                  <VersionRow
                    key={v._id || v.id}
                    version={v}
                    isCurrent={Boolean(v.is_current)}
                    onRevert={() => handleRevert(v)}
                    onDelete={() => handleDelete(v._id || v.id)}
                    onLabel={() => {
                      setLabelTarget(v._id || v.id);
                      setLabelValue(v.label || "");
                      setLabelDialogOpen(true);
                    }}
                  />
                ))
              ) : (
                <div className="text-center text-gray-400 text-xs py-4">
                  <Clock size={14} className="mx-auto mb-1 opacity-50" />
                  No version history yet.
                  <br />
                  Versions are created when you replace this asset.
                </div>
              )}
            </div>
          )}
            </CardContent>
            </Card>
          </div>
        </ContextMenuTrigger>
        <ContextMenuContent className="min-w-[160px]">
          <ContextMenuItem onClick={() => onRename(asset)}>
            {selectedCount > 1 ? "Rename all" : "Rename"}
          </ContextMenuItem>
          <ContextMenuSeparator />
          <ContextMenuItem
            className="text-red-400"
            onClick={() => onDelete(asset)}
          >
            {selectedCount > 1 ? "Delete all" : "Delete"}
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>

      {/* Label dialog */}
      <Dialog open={labelDialogOpen} onOpenChange={setLabelDialogOpen}>
        <DialogContent className="bg-gray-800 border-gray-700 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white">Label Version</DialogTitle>
            <DialogDescription className="text-gray-400">
              Add a label to identify this version (e.g. "before client review").
            </DialogDescription>
          </DialogHeader>
          <Input
            value={labelValue}
            onChange={(e) => setLabelValue(e.target.value)}
            placeholder="Version label..."
            className="bg-gray-700 border-gray-600 text-white"
            onKeyDown={(e) => e.key === "Enter" && handleLabelSave()}
          />
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="ghost"
              className="text-gray-300 hover:text-white hover:bg-gray-700"
              onClick={() => setLabelDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleLabelSave}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename dialog */}
      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent className="bg-gray-800 border-gray-700 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white">Rename Asset</DialogTitle>
            <DialogDescription className="text-gray-400">
              Enter a new name for this asset.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            placeholder="Asset name..."
            className="bg-gray-700 border-gray-600 text-white"
            onKeyDown={(e) => e.key === "Enter" && handleRename()}
          />
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="ghost"
              className="text-gray-300 hover:text-white hover:bg-gray-700"
              onClick={() => setRenameDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleRename}>
              Rename
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Assets Page ──────────────────────────────────────────────────────────────

type SkinComponent = {
  id: string;
  name?: string;
  type?: string;
  content?: Record<string, unknown>;
};

function extractMediaFromSkinComponents(skinComponents: SkinComponent[]): AssetItem[] {
  const items: AssetItem[] = [];

  for (const comp of skinComponents) {
    if (!comp.content) continue;

    if (comp.type === "image" && comp.content.src) {
      items.push({
        id: comp.id,
        name: comp.name || "Untitled image",
        entityType: "image",
        thumbnailUrl: comp.content.src as string,
      });
    }

    if (comp.type === "icon" && comp.content.src && !comp.content.lucideIcon) {
      items.push({
        id: comp.id,
        name: comp.name || "Untitled icon",
        entityType: "icon",
        thumbnailUrl: comp.content.src as string,
      });
    }
  }

  return items;
}

export default function Assets() {
  const { project, panoramas, skinComponents, hotspots, projectBackend, updatePanorama, updateSkinComponent, updateHotspot, uploadPanoramas, localLoading } = useEditorStore();
  const useLocalBackend =
    isElectron() && (projectBackend === "local" || (project?.id && isUuid(project.id)));
  const projectData = project?.data as Record<string, unknown> | null;
  const storageSegments = project
    ? resolveStoragePathSegments({
        craftspaceId: project.craftspace_id || "default",
        projectId: project.id,
        projectName: project.name,
        projectData,
        storagePath: typeof projectData?.storage_path === "string" ? projectData.storage_path : null,
      })
    : { craftspaceKey: "default", projectKey: "default" };
  const convex = useConvex();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showUploadPreferences, setShowUploadPreferences] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[] | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [panoramaSearchTerm, setPanoramaSearchTerm] = useState("");
  const [panoramasOpen, setPanoramasOpen] = useState(true);
  const [imagesOpen, setImagesOpen] = useState(true);
  const [iconsOpen, setIconsOpen] = useState(true);
  const [videosOpen, setVideosOpen] = useState(true);
  const [downloadsOpen, setDownloadsOpen] = useState(true);
  const dragDepthRef = useRef(0);

  // Selection and bulk operations state
  const [selectedAssets, setSelectedAssets] = useState<Set<string>>(new Set());
  const [selectedAsset, setSelectedAsset] = useState<AssetItem | null>(null);
  const [showBulkRenameDialog, setShowBulkRenameDialog] = useState(false);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [bulkRenameMode, setBulkRenameMode] = useState<"replace" | "add">("replace");
  const [bulkRenameFindText, setBulkRenameFindText] = useState("");
  const [bulkRenameReplaceText, setBulkRenameReplaceText] = useState("");
  const [bulkRenameAddText, setBulkRenameAddText] = useState("");
  const [bulkRenameAddPosition, setBulkRenameAddPosition] = useState<"before" | "after">("after");
  const [assetsToDelete, setAssetsToDelete] = useState<AssetItem[]>([]);

  useEffect(() => {
    if (!useLocalBackend || !project?.craftspace_id) return;
    let cancelled = false;

    const backfillSizes = async () => {
      console.log("[Assets] Backfill start", {
        panoramas: panoramas?.length ?? 0,
        craftspaceId: project.craftspace_id,
      });
      const craftspaceId = project.craftspace_id || "default";
      for (const panorama of panoramas || []) {
        if (typeof panorama.file_size === "number") continue;
        const storagePath =
          (panorama.private_file_url as string | undefined) ??
          (panorama.file_url as string | undefined);
        if (!storagePath) continue;
        console.log("[Assets] Stat panorama", panorama.id, storagePath);
        const size = await getLocalFileSize(storagePath);
        if (!cancelled && typeof size === "number") {
          console.log("[Assets] Update file_size", panorama.id, size);
          await updateLocalPanorama({ id: panorama.id, craftspace_id: craftspaceId, file_size: size });
        }
      }

      for (const hotspot of hotspots || []) {
        const content = (hotspot.content as Record<string, unknown>) || {};
        const downloadUrl = content.downloadFileUrl as string | undefined;
        if (!downloadUrl) continue;
        if (typeof content.downloadFileSize === "number") continue;
        console.log("[Assets] Stat download", hotspot.id, downloadUrl);
        const size = await getLocalFileSize(downloadUrl);
        if (!cancelled && typeof size === "number") {
          console.log("[Assets] Update downloadFileSize", hotspot.id, size);
          await updateLocalHotspot({
            id: hotspot.id,
            craftspace_id: craftspaceId,
            content: { ...content, downloadFileSize: size },
          });
        }
      }
      console.log("[Assets] Backfill done");
    };

    void backfillSizes();
    return () => {
      cancelled = true;
    };
  }, [useLocalBackend, panoramas, hotspots, project?.craftspace_id]);

  const handleFileInput = useCallback((e: { target: HTMLInputElement }) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      // Convert to array immediately — FileList is a live DOM reference that gets
      // invalidated when we clear the input value below.
      const fileArray = Array.from(files);
      e.target.value = "";
      setPendingFiles(fileArray);
      setShowUploadPreferences(true);
    } else {
      e.target.value = "";
    }
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragDepthRef.current += 1;
    setIsDragOver(true);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragDepthRef.current = 0;
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      setPendingFiles(Array.from(files));
      setShowUploadPreferences(true);
    }
  }, []);

  const handleUploadConfirm = useCallback(async (pref: UploadPreference) => {
    setShowUploadPreferences(false);
    if (pendingFiles && pendingFiles.length > 0) {
      // uploadPanoramas expects a FileList; reconstruct one via DataTransfer.
      const dt = new DataTransfer();
      for (const file of pendingFiles) dt.items.add(file);
      await uploadPanoramas(dt.files, pref);
      setPendingFiles(null);
    }
  }, [pendingFiles, uploadPanoramas]);


  if (!project) {
    return (
      <div className="h-full bg-gray-800 text-white flex items-center justify-center">
        <p className="text-gray-400">No project selected</p>
      </div>
    );
  }

  // Build asset list from panoramas
  const panoramaAssets: AssetItem[] = (panoramas || []).map((p) => ({
    id: p.id,
    name: p.name,
    entityType: "panorama" as const,
    thumbnailUrl: p.thumbnail_url || p.thumbnail_url_webp,
    width: p.width,
    height: p.height,
    fileSize: p.file_size,
  }));

  // Extract custom images and icons from skin components
  const mediaFromSkin = extractMediaFromSkinComponents(
    (skinComponents || []) as SkinComponent[],
  );
  const imageAssets = mediaFromSkin.filter((a) => a.entityType === "image");
  const iconAssets = mediaFromSkin.filter((a) => a.entityType === "icon");
  const videoAssets: AssetItem[] = (hotspots || [])
    .filter((h) => h.type === "video")
    .map((h) => ({
      id: h.id,
      name: h.name || "Video",
      entityType: "video" as const,
      content: (h.content as Record<string, unknown>) || {},
    }));
  const downloadAssets: AssetItem[] = (hotspots || [])
    .filter((h) => {
      const content = (h.content as Record<string, unknown>) || {};
      return Boolean(content.downloadFileUrl);
    })
    .map((h) => ({
      id: h.id,
      name:
        ((h.content as Record<string, unknown>)?.downloadFileName as string) ||
        h.name ||
        "Download",
      entityType: "download" as const,
      content: (h.content as Record<string, unknown>) || {},
      fileSize: (h.content as Record<string, unknown>)?.downloadFileSize as number | undefined,
    }));

  const sumFileSizes = (items: AssetItem[]) =>
    items.reduce((total, item) => total + (typeof item.fileSize === "number" ? item.fileSize : 0), 0);

  const panoramaTotalSize = sumFileSizes(panoramaAssets);
  const imageTotalSize = sumFileSizes(imageAssets);
  const iconTotalSize = sumFileSizes(iconAssets);
  const videoTotalSize = sumFileSizes(videoAssets);
  const downloadTotalSize = sumFileSizes(downloadAssets);
  const totalAssetSize =
    panoramaTotalSize + imageTotalSize + iconTotalSize + videoTotalSize + downloadTotalSize;

  const handleSelectAsset = useCallback(
    (asset: AssetItem, e: React.MouseEvent) => {
      e.stopPropagation();
      const allAssets = [
        ...panoramaAssets,
        ...imageAssets,
        ...iconAssets,
        ...videoAssets,
        ...downloadAssets,
      ];

      if (e.shiftKey && selectedAsset && selectedAsset.id !== asset.id) {
        const anchorIndex = allAssets.findIndex((a) => a.id === selectedAsset.id);
        const clickedIndex = allAssets.findIndex((a) => a.id === asset.id);
        if (anchorIndex !== -1 && clickedIndex !== -1) {
          const start = Math.min(anchorIndex, clickedIndex);
          const end = Math.max(anchorIndex, clickedIndex);
          const rangeIds = allAssets.slice(start, end + 1).map((a) => a.id);
          setSelectedAssets(new Set(rangeIds));
          return;
        }
      }
      setSelectedAsset(asset);
      setSelectedAssets(new Set([asset.id]));
    },
    [panoramaAssets, imageAssets, iconAssets, videoAssets, downloadAssets, selectedAsset],
  );

  const handleAssetRename = useCallback(
    (asset: AssetItem) => {
      // If multiple items are selected, open dialog with all of them
      // If single item, ensure it's selected then open dialog
      if (selectedAssets.size <= 1) {
        setSelectedAssets(new Set([asset.id]));
      }
      setShowBulkRenameDialog(true);
    },
    [selectedAssets],
  );

  const handleAssetDelete = useCallback(
    (asset: AssetItem) => {
      if (selectedAssets.size <= 1) {
        // Single asset - show confirmation for just this one
        setAssetsToDelete([asset]);
      } else {
        // Multiple selected - show bulk delete dialog
        const allAssets = [
          ...panoramaAssets,
          ...imageAssets,
          ...iconAssets,
          ...videoAssets,
          ...downloadAssets,
        ];
        const toDelete = allAssets.filter((a) => selectedAssets.has(a.id));
        setAssetsToDelete(toDelete);
      }
      setShowBulkDeleteDialog(true);
    },
    [selectedAssets, panoramaAssets, imageAssets, iconAssets, videoAssets, downloadAssets],
  );

  const handleBulkRename = useCallback(async () => {
    try {
      const allAssets = [
        ...panoramaAssets,
        ...imageAssets,
        ...iconAssets,
        ...videoAssets,
        ...downloadAssets,
      ];
      const assetsToRename = allAssets.filter((a) => selectedAssets.has(a.id));

      for (const asset of assetsToRename) {
        let newName = asset.name;

        if (bulkRenameMode === "replace" && bulkRenameFindText) {
          newName = newName.replaceAll(bulkRenameFindText, bulkRenameReplaceText);
        } else if (bulkRenameMode === "add" && bulkRenameAddText) {
          if (bulkRenameAddPosition === "before") {
            newName = bulkRenameAddText + newName;
          } else {
            newName = newName + bulkRenameAddText;
          }
        }

        if (newName !== asset.name) {
          try {
            const isLocal = useLocalBackend || (asset.entityType === "panorama" && isUuid(asset.id));
            if (asset.entityType === "panorama") {
              updatePanorama(asset.id, { name: newName });
              if (isLocal) {
                const wsId = project?.craftspace_id || "default";
                await updateLocalPanorama({ id: asset.id, craftspace_id: wsId, name: newName });
              } else {
                await convex.mutation(api.panoramas.update, { id: asset.id as any, name: newName });
              }
            } else if (asset.entityType === "image" || asset.entityType === "icon") {
              updateSkinComponent(asset.id, { name: newName });
              if (!isLocal) {
                await convex.mutation(api.skinComponents.update, { id: asset.id as any, name: newName });
              }
            } else if (asset.entityType === "video" || asset.entityType === "download") {
              updateHotspot(asset.id, { name: newName } as any);
              if (isLocal) {
                const wsId = project?.craftspace_id || "default";
                await updateLocalHotspot({ id: asset.id, craftspace_id: wsId, name: newName });
              } else {
                await convex.mutation(api.hotspots.update, { id: asset.id as any, name: newName });
              }
            }
          } catch (error) {
            console.error("Error renaming asset:", asset.name, error);
          }
        }
      }

      setShowBulkRenameDialog(false);
      setSelectedAssets(new Set());
      setBulkRenameFindText("");
      setBulkRenameReplaceText("");
      setBulkRenameAddText("");
    } catch (error) {
      console.error("Error in bulk rename:", error);
    }
  }, [
    convex,
    selectedAssets,
    panoramaAssets,
    imageAssets,
    iconAssets,
    videoAssets,
    downloadAssets,
    bulkRenameMode,
    bulkRenameFindText,
    bulkRenameReplaceText,
    bulkRenameAddText,
    bulkRenameAddPosition,
    skinComponents,
    useLocalBackend,
    project,
    updatePanorama,
    updateSkinComponent,
    updateHotspot,
  ]);

  const handleBulkDelete = useCallback(async () => {
    try {
      for (const asset of assetsToDelete) {
        try {
          if (asset.entityType === "panorama") {
            await convex.mutation(api.panoramas.remove, { id: asset.id });
          } else if (asset.entityType === "image" || asset.entityType === "icon") {
            // Skin components
            await convex.mutation(api.skinComponents.remove, { id: asset.id });
          } else if (asset.entityType === "video" || asset.entityType === "download") {
            // Hotspots
            await convex.mutation(api.hotspots.remove, { id: asset.id });
          }
        } catch (error) {
          console.error("Error deleting asset:", asset.name, error);
        }
      }

      setShowBulkDeleteDialog(false);
      setSelectedAssets(new Set());
      setAssetsToDelete([]);
    } catch (error) {
      console.error("Error in bulk delete:", error);
    }
  }, [convex, assetsToDelete]);

  return (
    <>
    <div className="h-full bg-gray-800 text-white overflow-auto">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <AssetsIcon size={20} className="text-white" />
            <div>
              <h1 className="text-2xl font-bold text-white">Assets</h1>
              <p className="text-sm text-gray-400">
                Manage project media and version history
              </p>
              <p className="text-xs text-gray-400">
                Total size: {formatFileSize(totalAssetSize)}
              </p>
            </div>
          </div>
          {selectedAssets.size > 0 && (
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowBulkRenameDialog(true)}
              >
                Rename ({selectedAssets.size})
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => {
                  const allAssets = [
                    ...panoramaAssets,
                    ...imageAssets,
                    ...iconAssets,
                    ...videoAssets,
                    ...downloadAssets,
                  ];
                  const toDelete = allAssets.filter((a) => selectedAssets.has(a.id));
                  setAssetsToDelete(toDelete);
                  setShowBulkDeleteDialog(true);
                }}
              >
                Delete ({selectedAssets.size})
              </Button>
            </div>
          )}
        </div>

        {/* Panoramas section */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <button
              type="button"
              onClick={() => setPanoramasOpen((o) => !o)}
              className="flex items-center gap-2 flex-1 text-left group"
            >
              <ChevronDown size={14} className={`text-gray-400 transition-transform ${panoramasOpen ? '' : '-rotate-90'}`} />
              <FileImage size={14} className="text-gray-400" />
              <h2 className="text-sm font-medium text-gray-300">
                Panoramas ({panoramaAssets.length})
              </h2>
              <span className="text-xs text-gray-400">
                {formatFileSize(panoramaTotalSize)}
              </span>
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded text-xs text-gray-300 bg-gray-700 hover:bg-gray-600 transition-colors"
            >
              <Upload size={12} />
              Upload
            </button>
          </div>

          {panoramasOpen && (
            <>
              <div className="relative flex items-center mb-3">
                <Search size={12} className="absolute left-2 text-gray-500 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search panoramas..."
                  value={panoramaSearchTerm}
                  onChange={(e) => setPanoramaSearchTerm(e.target.value)}
                  className="w-full pl-6 pr-6 py-1 text-xs bg-gray-700 text-white placeholder-gray-500 rounded focus:outline-none"
                />
                {panoramaSearchTerm && (
                  <button
                    type="button"
                    onClick={() => setPanoramaSearchTerm("")}
                    className="absolute right-2 text-gray-400 hover:text-gray-200 transition-colors"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
              <div
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`rounded-lg transition-colors ${isDragOver ? "ring-2 ring-blue-500 bg-blue-500/10" : ""}`}
              >
                {panoramaAssets.length === 0 && localLoading ? (
                  <div className="flex items-center justify-center py-12 text-gray-500">
                    <RefreshCw size={16} className="animate-spin mr-2" />
                    <span className="text-sm">Loading panoramas…</span>
                  </div>
                ) : panoramaAssets.length === 0 ? (
                  <div
                    className="flex flex-col items-center justify-center gap-3 py-12 border-2 border-dashed rounded-lg cursor-pointer transition-colors border-gray-600 hover:border-gray-500 text-gray-400"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <FolderOpen size={32} className="opacity-50" />
                    <div className="text-center">
                      <p className="text-sm">Drop panoramas here or click to upload</p>
                      <p className="text-xs text-gray-500 mt-1">Supports JPEG, PNG, and WebP equirectangular images</p>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                    {panoramaAssets
                      .filter((a) => a.name.toLowerCase().includes(panoramaSearchTerm.toLowerCase()))
                      .map((asset) => (
                        <AssetCard
                          key={asset.id}
                          asset={asset}
                          useLocalBackend={useLocalBackend}
                          isSelected={selectedAssets.has(asset.id)}
                          selectedCount={selectedAssets.size}
                          onSelect={handleSelectAsset}
                          onRename={handleAssetRename}
                          onDelete={handleAssetDelete}
                        />
                      ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Images section */}
        {imageAssets.length > 0 && (
          <div>
            <button
              type="button"
              onClick={() => setImagesOpen((o) => !o)}
              className="flex items-center gap-2 mb-3 w-full text-left"
            >
              <ChevronDown size={14} className={`text-gray-400 transition-transform ${imagesOpen ? '' : '-rotate-90'}`} />
              <Image size={14} className="text-gray-400" />
              <h2 className="text-sm font-medium text-gray-300">
                Images ({imageAssets.length})
              </h2>
              <span className="text-xs text-gray-400">
                {formatFileSize(imageTotalSize)}
              </span>
            </button>
            {imagesOpen && (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                {imageAssets.map((asset) => (
                  <AssetCard
                    key={asset.id}
                    asset={asset}
                    useLocalBackend={useLocalBackend}
                    isSelected={selectedAssets.has(asset.id)}
                    selectedCount={selectedAssets.size}
                    onSelect={handleSelectAsset}
                    onRename={handleAssetRename}
                    onDelete={handleAssetDelete}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Icons section */}
        {iconAssets.length > 0 && (
          <div>
            <button
              type="button"
              onClick={() => setIconsOpen((o) => !o)}
              className="flex items-center gap-2 mb-3 w-full text-left"
            >
              <ChevronDown size={14} className={`text-gray-400 transition-transform ${iconsOpen ? '' : '-rotate-90'}`} />
              <Sparkles size={14} className="text-gray-400" />
              <h2 className="text-sm font-medium text-gray-300">
                Custom Icons ({iconAssets.length})
              </h2>
              <span className="text-xs text-gray-400">
                {formatFileSize(iconTotalSize)}
              </span>
            </button>
            {iconsOpen && (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                {iconAssets.map((asset) => (
                  <AssetCard
                    key={asset.id}
                    asset={asset}
                    useLocalBackend={useLocalBackend}
                    isSelected={selectedAssets.has(asset.id)}
                    selectedCount={selectedAssets.size}
                    onSelect={handleSelectAsset}
                    onRename={handleAssetRename}
                    onDelete={handleAssetDelete}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {videoAssets.length > 0 && (
          <div>
            <button
              type="button"
              onClick={() => setVideosOpen((o) => !o)}
              className="flex items-center gap-2 mb-3 w-full text-left"
            >
              <ChevronDown size={14} className={`text-gray-400 transition-transform ${videosOpen ? '' : '-rotate-90'}`} />
              <Film size={14} className="text-gray-400" />
              <h2 className="text-sm font-medium text-gray-300">
                Videos ({videoAssets.length})
              </h2>
              <span className="text-xs text-gray-400">
                {formatFileSize(videoTotalSize)}
              </span>
            </button>
            {videosOpen && (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                {videoAssets.map((asset) => (
                  <AssetCard
                    key={asset.id}
                    asset={asset}
                    useLocalBackend={useLocalBackend}
                    isSelected={selectedAssets.has(asset.id)}
                    selectedCount={selectedAssets.size}
                    onSelect={handleSelectAsset}
                    onRename={handleAssetRename}
                    onDelete={handleAssetDelete}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {downloadAssets.length > 0 && (
          <div>
            <button
              type="button"
              onClick={() => setDownloadsOpen((o) => !o)}
              className="flex items-center gap-2 mb-3 w-full text-left"
            >
              <ChevronDown size={14} className={`text-gray-400 transition-transform ${downloadsOpen ? '' : '-rotate-90'}`} />
              <Download size={14} className="text-gray-400" />
              <h2 className="text-sm font-medium text-gray-300">
                Downloads ({downloadAssets.length})
              </h2>
              <span className="text-xs text-gray-400">
                {formatFileSize(downloadTotalSize)}
              </span>
            </button>
            {downloadsOpen && (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                {downloadAssets.map((asset) => (
                  <AssetCard
                    key={asset.id}
                    asset={asset}
                    useLocalBackend={useLocalBackend}
                    isSelected={selectedAssets.has(asset.id)}
                    selectedCount={selectedAssets.size}
                    onSelect={handleSelectAsset}
                    onRename={handleAssetRename}
                    onDelete={handleAssetDelete}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>

    {/* Hidden file input for panorama upload */}
    <input
      ref={fileInputRef}
      type="file"
      accept="image/*"
      multiple
      className="hidden"
      onChange={handleFileInput}
    />

    <UploadPreferencesDialog
      isOpen={showUploadPreferences}
      onClose={() => { setShowUploadPreferences(false); setPendingFiles(null); }}
      onConfirm={handleUploadConfirm}
      fileCount={pendingFiles?.length ?? 0}
    />

    {/* Bulk Rename Dialog */}
    <Dialog open={showBulkRenameDialog} onOpenChange={setShowBulkRenameDialog}>
      <DialogContent className="bg-gray-800 border-gray-700 max-w-lg w-full">
        <DialogHeader>
          <DialogTitle className="text-white">Rename Assets</DialogTitle>
        </DialogHeader>

        <Tabs
          value={bulkRenameMode}
          onValueChange={(value) => setBulkRenameMode(value as "replace" | "add")}
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-2 bg-gray-700">
            <TabsTrigger
              value="replace"
              className="text-gray-300 data-[state=active]:text-white data-[state=active]:bg-gray-600"
            >
              Replace Text
            </TabsTrigger>
            <TabsTrigger
              value="add"
              className="text-gray-300 data-[state=active]:text-white data-[state=active]:bg-gray-600"
            >
              Add Text
            </TabsTrigger>
          </TabsList>

          <TabsContent value="replace" className="space-y-3 mt-4 focus-visible:ring-0 focus-visible:outline-none">
            <div>
              <Label htmlFor="find-text" className="text-gray-300 text-sm mb-2 block">
                Find:
              </Label>
              <Input
                id="find-text"
                autoFocus
                value={bulkRenameFindText}
                onChange={(e) => setBulkRenameFindText(e.target.value)}
                placeholder="Text to find"
                className="bg-gray-700 border-0 text-white placeholder:text-gray-500 focus-visible:ring-1 focus-visible:ring-blue-500 focus-visible:ring-offset-0"
              />
            </div>
            <div>
              <Label htmlFor="replace-with-text" className="text-gray-300 text-sm mb-2 block">
                Replace with:
              </Label>
              <Input
                id="replace-with-text"
                value={bulkRenameReplaceText}
                onChange={(e) => setBulkRenameReplaceText(e.target.value)}
                placeholder="Replacement text"
                className="bg-gray-700 border-0 text-white placeholder:text-gray-500 focus-visible:ring-1 focus-visible:ring-blue-500 focus-visible:ring-offset-0"
              />
            </div>
          </TabsContent>

          <TabsContent value="add" className="space-y-4 mt-4 focus-visible:ring-0 focus-visible:outline-none">
            <div className="space-y-2">
              <Label className="text-gray-300">Add Text:</Label>
              <div className="flex gap-2">
                <Input
                  value={bulkRenameAddText}
                  onChange={(e) => setBulkRenameAddText(e.target.value)}
                  placeholder="Text to add"
                  className="bg-gray-700 border-0 text-white placeholder:text-gray-500 focus-visible:ring-1 focus-visible:ring-blue-500 focus-visible:ring-offset-0 flex-1"
                />
                <select
                  value={bulkRenameAddPosition}
                  onChange={(e) =>
                    setBulkRenameAddPosition(e.target.value as "before" | "after")
                  }
                  className="bg-gray-700 border border-gray-600 text-white px-3 py-2 rounded text-sm"
                >
                  <option value="before">before name</option>
                  <option value="after">after name</option>
                </select>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Preview */}
        {selectedAssets.size > 0 && (
          <div className="bg-gray-700 rounded p-3 space-y-2">
            <p className="text-xs text-gray-400 font-semibold">Preview:</p>
            {Array.from(selectedAssets)
              .slice(0, 3)
              .map((assetId) => {
                const allAssets = [
                  ...panoramaAssets,
                  ...imageAssets,
                  ...iconAssets,
                  ...videoAssets,
                  ...downloadAssets,
                ];
                const asset = allAssets.find((a) => a.id === assetId);
                if (!asset) return null;

                let newName = asset.name;
                if (bulkRenameMode === "replace" && bulkRenameFindText) {
                  newName = newName.replaceAll(bulkRenameFindText, bulkRenameReplaceText);
                } else if (bulkRenameMode === "add" && bulkRenameAddText) {
                  if (bulkRenameAddPosition === "before") {
                    newName = bulkRenameAddText + newName;
                  } else {
                    newName = newName + bulkRenameAddText;
                  }
                }
                return (
                  <div key={asset.id} className="text-xs">
                    <span className="text-gray-400">{asset.name}</span>
                    {newName !== asset.name && (
                      <>
                        <span className="text-gray-500 mx-1">→</span>
                        <span className="text-white">{newName}</span>
                      </>
                    )}
                  </div>
                );
              })}
            {selectedAssets.size > 3 && (
              <p className="text-xs text-gray-400">
                ... and {selectedAssets.size - 3} more
              </p>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setShowBulkRenameDialog(false);
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => handleBulkRename()}
            disabled={
              (bulkRenameMode === "replace" && !bulkRenameFindText) ||
              (bulkRenameMode === "add" && !bulkRenameAddText)
            }
          >
            Rename
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Bulk Delete Dialog */}
    <Dialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
      <DialogContent className="bg-gray-800 border-gray-700 max-w-sm w-full">
        <DialogHeader>
          <DialogTitle className="text-white">Delete {assetsToDelete.length} Assets?</DialogTitle>
          <DialogDescription className="text-gray-400">
            This action cannot be undone. All selected assets will be permanently deleted.
          </DialogDescription>
        </DialogHeader>
        <div className="text-gray-300 mb-4">
          Are you sure you want to delete {assetsToDelete.length} asset(s)? This action cannot be undone.
        </div>
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setShowBulkDeleteDialog(false);
              setAssetsToDelete([]);
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            autoFocus
            onClick={() => handleBulkDelete()}
          >
            Delete All
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
